import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import JumboDemoCard from "@jumbo/components/JumboDemoCard/JumboDemoCard";
import {
  ApproveUpgradeRequestApi,
  GetUpgradeRequestsReport,
  RejectUpgradeRequestApi,
} from "backendServices/ApiCalls";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import SweetAlert from "app/pages/components/mui/Alerts/SweetAlert";
import RecommendIcon from "@mui/icons-material/Recommend";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import { useTranslation } from "react-i18next";

/**
 * The "invoice me" queue from the customer-facing upgrade page — see
 * controllers/billing/upgradeRequestController.js on the API. Approving one
 * calls helpers/planActivation.js on the backend, the same code the
 * `plan:activate` CLI uses, at the amount the customer was quoted on the
 * pricing card — never a value typed in here.
 */
const Upgraderequestpending = () => {
  const { t } = useTranslation();
  const [report, setReport] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rowData, setRowData] = useState(null);
  const [reason, setReason] = useState("");

  const [alertData, setalertData] = useState({ show: false, message: "", variant: "" });

  const fetchReport = () => {
    GetUpgradeRequestsReport(
      (response) => {
        const pending = (response?.data?.data || []).filter((row) => row.status === "Pending");
        setReport(pending.map((row, index) => ({ ...row, index: index + 1 })));
      },
      () => {}
    );
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const columns = [
    { field: "index", headerName: "#", width: 60 },
    {
      field: "name",
      headerName: "Customer",
      width: 180,
      valueGetter: (params) =>
        `${params.row.firstname || ""} ${params.row.lastname || ""}`.trim() || "—",
    },
    { field: "email", headerName: "Email", width: 220 },
    { field: "current_plan", headerName: "Current Plan", width: 120 },
    { field: "plan", headerName: "Wants", width: 110 },
    { field: "period", headerName: "Period", width: 100 },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      valueGetter: (params) =>
        params.row.amount != null ? `${params.row.amount} ${params.row.currency || ""}` : "—",
    },
    { field: "company", headerName: "Company", width: 150, valueGetter: (p) => p.value || "—" },
    { field: "vat_id", headerName: "VAT ID", width: 140, valueGetter: (p) => p.value || "—" },
    { field: "country", headerName: "Country", width: 130, valueGetter: (p) => p.value || "—" },
    { field: "notes", headerName: "Notes", width: 220, valueGetter: (p) => p.value || "—" },
    {
      field: "createdAt",
      headerName: "Requested",
      width: 170,
      valueGetter: (p) => (p.value ? new Date(p.value).toLocaleString() : "N/A"),
    },
    {
      field: "approve",
      headerName: "Approve",
      width: 100,
      renderCell: (params) => (
        <RecommendIcon
          onClick={() => handleApprove(params.row.id)}
          style={{ cursor: "pointer", color: "green" }}
        />
      ),
    },
    {
      field: "reject",
      headerName: "Reject",
      width: 100,
      renderCell: (params) => (
        <ThumbDownAltIcon
          onClick={() => handleRejectOpen(params.row)}
          style={{ cursor: "pointer", color: "red" }}
        />
      ),
    },
  ];

  const handleApprove = (id) => {
    ApproveUpgradeRequestApi(
      { id },
      (response) => {
        if (response?.data?.status === "success") {
          setalertData({ show: true, message: response?.data?.message, variant: "success" });
          setReport((rows) => rows.filter((row) => row.id !== id));
        } else {
          setalertData({
            show: true,
            message: response?.data?.message || "Something went wrong.",
            variant: "error",
          });
        }
      },
      () => setalertData({ show: true, message: "Something went wrong.", variant: "error" })
    );
  };

  const handleRejectOpen = (row) => {
    setRowData(row);
    setReason("");
    setRejectOpen(true);
  };

  const handleRejectClose = () => setRejectOpen(false);

  const handleReject = () => {
    RejectUpgradeRequestApi(
      { id: rowData.id, reason },
      (response) => {
        setRejectOpen(false);
        if (response?.data?.status === "success") {
          setalertData({ show: true, message: response?.data?.message, variant: "success" });
          setReport((rows) => rows.filter((row) => row.id !== rowData.id));
        } else {
          setalertData({
            show: true,
            message: response?.data?.message || "Something went wrong.",
            variant: "error",
          });
        }
      },
      () => {
        setRejectOpen(false);
        setalertData({ show: true, message: "Something went wrong.", variant: "error" });
      }
    );
  };

  const gridDesign = {
    "& .MuiDataGrid-toolbarContainer": {
      "& .MuiButton-text": { fontSize: "13px !important" },
      "& .MuiBadge-badge": { backgroundColor: "#074682" },
      "& .MuiInput-root": { borderRadius: 2, paddingLeft: 2, overflow: "hidden" },
    },
  };

  return (
    <JumboDemoCard
      title={t("pages.title.upgraderequestpending")}
      wrapperSx={{ backgroundColor: "background.paper", pt: 0 }}
    >
      {alertData.show && <SweetAlert alertData={alertData} setalertData={setalertData} />}
      <Box sx={{ height: 550, width: 1 }}>
        <DataGrid
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rows={report}
          getRowId={(row) => row.id}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          sx={gridDesign}
          pageSizeOptions={[10, 25, 50]}
          slotProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } } }}
        />
      </Box>

      <Dialog open={rejectOpen} onClose={handleRejectClose}>
        <DialogTitle>Reject upgrade request</DialogTitle>
        <DialogContent>
          <TextField
            id="reject-reason"
            label="Reason (optional)"
            variant="outlined"
            fullWidth
            multiline
            rows={3}
            style={{ marginTop: 5, width: 400 }}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={handleReject}>
            Reject
          </Button>
          <Button variant="contained" color="inherit" onClick={handleRejectClose} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </JumboDemoCard>
  );
};

export default Upgraderequestpending;
